import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import HoldIcon from "../assets/imgs/hold-icon.svg"
import TradeAwayIcon from "../assets/imgs/trade-way-icon.svg"
import TradeForIcon from "../assets/imgs/trade-for-icon.svg"
import WaiverPickupIcon from "../assets/imgs/waiver-pickup-icon.svg"
import DropIcon from "../assets/imgs/drop-icon.svg"
import { supabase } from "../supabase/supabase.config";
import create from "zustand";
import { persist } from "zustand/middleware";
import { useLeagueStore } from "../zustandStore/leagueStore";
import { usePaywallStore } from "../zustandStore/paywallStore";
import MixpanelService from "../Services/MixpanelService";
import { useAdvancedStatsPromoStore } from "../zustandStore/advancedStatsPromoStore";
import { useBookaCallStore } from "../zustandStore/bookaCallStore";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertDateFormat = (dateString: string) => {
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

export const parseDate = (dateString: string) => {
  const [day, month, year] = dateString.split(/[-/]/);
  return new Date(`${year}-${month}-${day}`);
};

export function snakeToTitleCase(snakeStr: string) {
  return snakeStr
    .split("_")
    .map(
      (word: string) =>
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
}

export const capitalizeFirstLetter = (text: string | undefined) => {
  try {
    if (!text) return "";
    
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  } catch (err) {
    console.log("error in capitalizeFirstLetter", err);
    return "";
  }
};

export const abbreviateName = (fullName: string): string => {
  const [firstName, lastName] = fullName.split(" ");
  if (!firstName || !lastName) return fullName;
  return `${firstName.charAt(0).toUpperCase()}. ${lastName}`;
};

export const getInitials = (fullName?: string | undefined | null) => {
  if (!fullName) {
    return "";
  }

  const nameParts = fullName.trim().split(" ");

  if (nameParts.length === 1) {
    return nameParts[0].charAt(0).toUpperCase();
  } else if (nameParts.length >= 2) {
    return (
      nameParts[0].charAt(0).toUpperCase() +
      nameParts[1].charAt(0).toUpperCase()
    );
  }
};

export const validateEmail = (email: string) => {
  const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return regex.test(email);
};

export const convertInchesToFeet = (inches: number): string => {
  if (!inches) return "--" 
  const feet = Math.floor(inches / 12);
  const remainingInches = inches % 12;
  const formattedInches =
    remainingInches === Math.floor(remainingInches)
      ? remainingInches.toString()
      : remainingInches.toFixed(1);
  return `${feet}'${formattedInches}`;
};

export const statusBarThemeColor = () => {
  let metaThemeColor = document.querySelector(
    'meta[name="theme-color"]',
  ) as HTMLMetaElement;

  if (!metaThemeColor) {
    metaThemeColor = document.createElement("meta");
    metaThemeColor.name = "theme-color";
    document.head.appendChild(metaThemeColor);
  }
  metaThemeColor.setAttribute("content", "#120F2C");
  return () => {
    metaThemeColor.setAttribute("content", "#ffff");
  };
};
export const commonHeaders = ["WK", "OPP", "SCORE", "FPTS", "RANK"];
export const passingHeaders = ["YDS", "TDS", "FUM", "ATT", "CMP", "INT"];
export const rushingHeaders = [
  "RSH ATT",
  "+ RSH ATT",
  "RSH YDS",
  "RSH TDS",
  "FUM",
];
export const receivingHeaders = [
  "TAR",
  "REC",
  "REC YDS",
  "REC TDS",
  "RET",
  "RET YDS",
];
export const returnHeaders = ["RET", "RET YDS", "RET TDS"];

export const renderQBStats = (stats: any, key: string) => {
  if (!stats || stats.length === 0) return "-"; // Handle missing stats
  switch (key) {
    case "YDS":
      return stats[0]?.pass_yards ? stats[0].pass_yards : "0";
    case "TDS":
      return stats[0]?.passing_touchdowns || "0";
    case "FUM":
      return stats[0]?.fumbles_lost || "0";
    case "ATT":
      return stats[0]?.pass_attempts || "0";
    case "CMP":
      return stats[0]?.completions || "0";
    case "INT":
      return stats[0]?.interceptions || "0";

    case "RSH YDS":
      return stats[1]?.rush_yards || "0";
    case "RSH TDS":
      return stats[1]?.rushing_touchdowns || "0";
    case "RSH ATT":
      return stats[1]?.rushing_attempts || "0";
    case "+ RSH ATT":
      return stats[1]?.successful_rush_attempts || "0";

    case "RET":
      return stats[1]?.returns || "0";
    case "RET YDS":
      return stats[1]?.return_yards || "0";
    case "RET TDS":
      return stats[1]?.return_tds || "0";

    default:
      return "-";
  }
};

export const renderRBStats = (stats: any, key: string) => {
  if (!stats || stats.length === 0) return "-"; // Handle missing stats
  switch (key) {
    case "RSH YDS":
      return stats[0]?.rush_yards || "0";
    case "RSH TDS":
      return stats[0]?.rushing_touchdowns || "0";
    case "FUM":
      return stats[0]?.fumbles_lost || "0";
    case "RSH ATT":
      return stats[0]?.rushing_attempts || "0";
    case "+ RSH ATT":
      return stats[0]?.successful_rush_attempts || "0";

    case "TAR":
      return stats[1]?.receiving_targets || "0";
    case "REC":
      return stats[1]?.receptions || "0";
    case "REC YDS":
      return stats[1]?.receiving_yards || "0";
    case "REC TDS":
      return stats[1]?.receiving_touchdowns || "0";

    case "RET":
      return stats[0]?.returns || "0";
    case "RET YDS":
      return stats[0]?.return_yards || "0";
    case "RET TDS":
      return stats[0]?.return_tds || "0";

    default:
      return "-";
  }
};

export const renderWR_TEStats = (stats: any, key: string) => {
  if (!stats || stats.length === 0) return "-"; // Handle missing stats
  switch (key) {
    case "REC YDS":
      return stats[0]?.receiving_yards || "0";
    case "REC TDS":
      return stats[0]?.receiving_touchdowns || "0";
    case "FUM":
      return stats[0]?.fumbles_lost || "0";
    case "TAR":
      return stats[0]?.receiving_targets || "0";
    case "REC":
      return stats[0]?.receptions || "0";

    case "RSH YDS":
      return stats[1]?.rush_yards || "0";
    case "RSH TDS":
      return stats[1]?.rushing_touchdowns || "0";
    case "RSH ATT":
      return stats[1]?.rushing_attempts || "0";
    case "+ RSH ATT":
      return stats[1]?.successful_rush_attempts || "0";

    case "RET":
      return stats[1]?.returns || "0";
    case "RET YDS":
      return stats[1]?.return_yards || "0";
    case "RET TDS":
      return stats[1]?.return_tds || "0";

    default:
      return "-";
  }
};

// Helper function to round numeric values or return fallback
export const roundOrDefault = (value: any, defaultValue = "-"): string | number => {
  return value != null ? Math.round(value) : defaultValue;
};

// Helper function to round numeric values to a specific decimal place or return fallback
export const roundToDecimalPlace = (value: any, decimalPlaces = 0, defaultValue = "-"): string | number => {
  if (value == null) return defaultValue;
  
  const factor = Math.pow(10, decimalPlaces);
  const rounded = Math.round(value * factor) / factor;
  
  // If decimalPlaces is 0, return as integer
  if (decimalPlaces === 0) return Math.round(value);
  
  // Format to ensure decimal places are shown even if they're zeros
  return rounded.toFixed(decimalPlaces);
};

export const fetchFieldValue = (item: any, header: string) => {
  if (!item) return "-";

  switch (header) {
    case "Player Name":
      return item.playerName || "-";
    case "Player":
      return item.playerName || "-";
    case "Pos":
      return item.playerPosition || "-";
    case "Team":
      return item.playerTeamShort || "-";
    case "ADP":
      return item.ADP_2025 || "-";
    case "Dynasty Score":
      return roundOrDefault(item.dynasty_value);
    case "P Att":
      return roundOrDefault(item.A);
    case "P Cmp":
      return roundOrDefault(item.C);
    case "P Yds":
      return roundOrDefault(item.PASS_YDS);
    case "P TD":
      return roundOrDefault(item.PASS_TD);
    case "Pass Yds":
      return roundOrDefault(item.PASS_YDS);
    case "Pass TD":
      return roundOrDefault(item.PASS_TD);
    case "Int":
      return roundOrDefault(item.INT);
    case "Sacks":
      return roundOrDefault(item.SACKS);
    case "P Yds/Att":
      return roundOrDefault(item.PASS_YDS_PER_ATTEMPT);
    case "Rush Att":
      return roundOrDefault(item.CAR);
    case "Rush Yds":
      return roundOrDefault(item.RUSH_YDS);
    case "Rush TD":
      return roundOrDefault(item.RUSH_TD);
    case "Touches":
      return roundOrDefault(item.TOTAL_TOUCHES);
    case "Targets":
      return roundOrDefault(item.TAR);
    case "Rec":
      return roundOrDefault(item.REC);
    case "Rec Yds":
      return roundOrDefault(item.REC_YDS);
    case "Rec TD":
      return roundOrDefault(item.REC_TD);
    case "FPTS (PPR)":
      return roundOrDefault(item.FPTS_PPR);
    case "Rush %":
      return roundOrDefault(item.RUSH_SHARE);
    case "Carries":
      return roundOrDefault(item.CAR);
    case "Yds/Carry":
      return roundOrDefault(item.YPC);
    case "FPTS (Std)":
      return roundOrDefault(item.FPTS_NON_PPR);
    case "Tgt %":
      return roundOrDefault(item.TARGET_SHARE);
    case "Catch %":
      return roundOrDefault(item.CATCH_RATE);
    case "Yds/Rec":
      return roundOrDefault(item.YPR);
    case "Yds/Tgt":
      return roundOrDefault(item.REC_YDS_PER_TARGET);
    case "Half PPR Pts":
      return roundToDecimalPlace(item.Production_Half_PPR_Points, 1);
    
    // 2024 QB Stats
    case "Pass Attempts":
      return roundOrDefault(item.Pass_Attempts);
    case "Completions":
      return roundOrDefault(item.Pass_Completions);
    case "Passing Yards":
      return roundOrDefault(item.Passing_Yards);
    case "Passing TDs":
      return roundOrDefault(item.Passing_TDs);
    case "Interceptions":
      return roundOrDefault(item.Interceptions);
    case "Completion %":
      return roundToDecimalPlace(item["Completion_%"], 1);
    case "Rush Attempts":
      return roundOrDefault(item.Rush_Attempts);
    
    // 2024 RB Stats
    case "Rushing Attempts":
      return roundOrDefault(item.Rush_Attempts);
    case "Rushing Yards":
      return roundOrDefault(item.Rush_Yards);
    case "Rushing TDs":
      return roundOrDefault(item.Rushing_TDs);
    case "Yds per Rush":
      return roundToDecimalPlace(item.Yds_per_Rush, 1);
    case "Receptions":
      return roundOrDefault(item.Receptions);
    case "Receiving Yards":
      return roundOrDefault(item.Receiving_Yards);
    case "Team Rush Share":
      return roundToDecimalPlace(item.Team_Rush_Share, 1);
    
    // 2024 WR/TE Stats
    case "Target Total":
      return roundOrDefault(item.Targets);
    case "Reception Total":
      return roundOrDefault(item.Receptions);
    case "Rec Yards Total":
      return roundOrDefault(item.Receiving_Yards);
    case "Receiving TDs":
      return roundOrDefault(item.Receiving_TDs);
    case "Catch Rate":
      return roundToDecimalPlace(item.Catch_Rate, 1);
    case "Avg Depth":
      return roundToDecimalPlace(item.Avg_Depth_of_Target, 1);
    case "Team Target Share":
      return roundToDecimalPlace(item.Team_Target_Share, 1);
    case "Avg YAC":
      return roundToDecimalPlace(item.Avg_YAC, 1);
    case "Full PPR Points":
      return roundToDecimalPlace(item.Full_PPR_Points, 1);
    
    default:
      return "-";
  }
};

export const formatDate = (dateString: string) => {
  const inputDate = new Date(dateString);
  const currentDate = new Date();

  const timeDifference = currentDate.getTime() - inputDate.getTime();

  const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

  if (daysDifference === 0) {
    const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));

    return hoursDifference < 24 ? "Today" : "1d";
  } else if (daysDifference > 0) {
    return `${daysDifference}d`;
  }
};

export const headerAccordingPlayerPosition = (playerPosition: string) => {
  switch (playerPosition) {
    case "QB":
      return [
        { id: 1, name: "PASSING" },
        { id: 2, name: "RUSHING" },
      ];
    case "RB":
      return [
        { id: 1, name: "RUSHING" },
        { id: 2, name: "RECEIVING" },
      ];
    case "WR":
    case "TE":
      return [
        { id: 1, name: "RECEIVING" },
        { id: 2, name: "RUSHING" },
      ];
    default:
      return [
        { id: 1, name: "PASSING" },
        { id: 2, name: "RUSHING" },
      ];
  }
};

export const getSuggestionsTitle = (name: string) => {
  switch (name) {
    case "season_outlook":
      return "Season Outlook";
    case "season_recap":
      return "Season Recap";
    case "trade_analyzer":
      return "Trade Analysis";
    case "waiver_wire":
      return "Waiver Assistant";
    case "sit_start":
      return "Sit/Start Analysis";
    case "weekly_outlook":
      return "Weekly Outlook";
    default:
      return "Suggested Question";
  }
};

export const getFormattedDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const options = { month: "long" } as const;
  const monthName = new Intl.DateTimeFormat("en-US", options).format(date);
  const day = date.getDate();

  const getOrdinalSuffix = (n: number): string => {
    const j = n % 10,
      k = n % 100;
    if (j === 1 && k !== 11) {
      return n + "st";
    }
    if (j === 2 && k !== 12) {
      return n + "nd";
    }
    if (j === 3 && k !== 13) {
      return n + "rd";
    }
    return n + "th";
  };

  const dayWithSuffix = getOrdinalSuffix(day);
  return `${monthName} ${dayWithSuffix}`;
};


export const hapticsImpactLight = async () => {
  console.log("in haptic");
  
  await Haptics.impact({ style: ImpactStyle.Light });
};


export const getLabelAndScore = (key: string) => {
  switch (key) {
    case "hold":
      return { label: "Hold", score: HoldIcon };
    case "trade away":
      return { label: "Sell", score: TradeAwayIcon };
    case "trade for":
      return { label: "Buy", score: TradeForIcon };
    case "waiver pickup":
      return { label: "Add", score: WaiverPickupIcon };
    case "drop":
      return { label: "Drop", score: DropIcon };
    default:
      return { label: "Hold", score: HoldIcon };
  }
};


export const mapWithFallback  = (array: any[]) => {
  if (Array.isArray(array)) {
    return array;    
  } else {
    return [];
  }
}


export const markdownStyleOverrides = {
  // Block elements
  p: { props: { className: 'text-[16px]' } },
  h1: { props: { className: 'text-[20px]' } },
  h2: { props: { className: 'text-[19px]' } },
  h3: { props: { className: 'text-[18px]' } },
  h4: { props: { className: 'text-[17px]' } },
  h5: { props: { className: 'text-[16px]' } },
  h6: { props: { className: 'text-[16px]' } },
  blockquote: { props: { className: 'text-[16px]' } },
  pre: { props: { className: 'text-[16px]' } },
  
  // List elements
  ul: { props: { className: 'text-[16px]' } },
  ol: { props: { className: 'text-[16px]' } },
  li: { props: { className: 'text-[16px]' } },
  dl: { props: { className: 'text-[16px]' } },
  dt: { props: { className: 'text-[16px]' } },
  dd: { props: { className: 'text-[16px]' } },
  
  // Table elements
  table: { props: { className: 'text-[12px]' } },
  thead: { props: { className: 'text-[12px]' } },
  tbody: { props: { className: 'text-[12px]' } },
  tfoot: { props: { className: 'text-[12px]' } },
  tr: { props: { className: 'text-[12px]' } },
  th: { props: { className: 'text-[12px]' } },
  td: { props: { className: 'text-[12px]' } },
  
  // Inline elements
  a: { props: { className: 'text-[16px]' } },
  em: { props: { className: 'text-[16px]' } },
  strong: { props: { className: 'text-[16px]' } },
  code: { props: { className: 'text-[15px]' } },
  span: { props: { className: 'text-[16px]' } },
  b: { props: { className: 'text-[16px]' } },
  i: { props: { className: 'text-[16px]' } },
  u: { props: { className: 'text-[16px]' } },
  small: { props: { className: 'text-[14px]' } },
  
  // Other elements
  img: { props: { className: '' } }, // No text size for images
  hr: { props: { className: '' } }, // No text size for horizontal rules
  br: { props: { className: '' } }, // No text size for line breaks
  sub: { props: { className: 'text-[14px]' } },
  sup: { props: { className: 'text-[14px]' } },
  div: { props: { className: 'text-[16px]' } },
  
  // Form elements (less common in Markdown but possible)
  button: { props: { className: 'text-[16px]' } },
  input: { props: { className: 'text-[16px]' } },
  label: { props: { className: 'text-[16px]' } },
  select: { props: { className: 'text-[16px]' } },
  option: { props: { className: 'text-[16px]' } },
  textarea: { props: { className: 'text-[16px]' } },
};


export const newMarkdownStyleOverrides = {
  // Block elements
  p: { props: { className: 'text-[13px] m-0 p-0' } },
  h1: { props: { className: 'text-[13px] m-0 p-0' } },
  h2: { props: { className: 'text-[13px] m-0 p-0' } },
  h3: { props: { className: 'text-[13px] m-0 p-0' } },
  h4: { props: { className: 'text-[13px] m-0 p-0' } },
  h5: { props: { className: 'text-[13px] m-0 p-0' } },
  h6: { props: { className: 'text-[13px] m-0 p-0' } },
  blockquote: { props: { className: 'text-[13px] m-0 p-0' } },
  pre: { props: { className: 'text-[13px] m-0 p-0' } },
  
  // List elements
  ul: { props: { className: 'text-[13px] m-0 p-0' } },
  ol: { props: { className: 'text-[13px] m-0 p-0' } },
  li: { props: { className: 'text-[13px] m-0 p-0' } },
  dl: { props: { className: 'text-[13px] m-0 p-0' } },
  dt: { props: { className: 'text-[13px] m-0 p-0' } },
  dd: { props: { className: 'text-[13px] m-0 p-0' } },
  
  // Table elements
  table: { props: { className: 'text-[12px] m-0 p-0' } },
  thead: { props: { className: 'text-[12px] m-0 p-0' } },
  tbody: { props: { className: 'text-[12px] m-0 p-0' } },
  tfoot: { props: { className: 'text-[12px] m-0 p-0' } },
  tr: { props: { className: 'text-[12px] m-0 p-0' } },
  th: { props: { className: 'text-[12px] m-0 p-0' } },
  td: { props: { className: 'text-[12px] m-0 p-0' } },
  
  // Inline elements
  a: { props: { className: 'text-[13px] m-0 p-0' } },
  em: { props: { className: 'text-[13px] m-0 p-0' } },
  strong: { props: { className: 'text-[13px] m-0 p-0' } },
  code: { props: { className: 'text-[13px] m-0 p-0' } },
  span: { props: { className: 'text-[13px] m-0 p-0' } },
  b: { props: { className: 'text-[13px] m-0 p-0' } },
  i: { props: { className: 'text-[13px] m-0 p-0' } },
  u: { props: { className: 'text-[13px] m-0 p-0' } },
  small: { props: { className: 'text-[13px] m-0 p-0' } },
  
  // Other elements
  img: { props: { className: 'm-0 p-0' } }, // No text size for images, but reset margin/padding
  hr: { props: { className: 'm-0 p-0' } }, // No text size for horizontal rules, but reset margin/padding
  br: { props: { className: 'm-0 p-0' } }, // No text size for line breaks, but reset margin/padding
  sub: { props: { className: 'text-[13px] m-0 p-0' } },
  sup: { props: { className: 'text-[13px] m-0 p-0' } },
  div: { props: { className: 'text-[13px] m-0 p-0' } },
  
  // Form elements (less common in Markdown but possible)
  button: { props: { className: 'text-[13px] m-0 p-0' } },
  input: { props: { className: 'text-[13px] m-0 p-0' } },
  label: { props: { className: 'text-[13px] m-0 p-0' } },
  select: { props: { className: 'text-[13px] m-0 p-0' } },
  option: { props: { className: 'text-[13px] m-0 p-0' } },
  textarea: { props: { className: 'text-[13px] m-0 p-0' } },
};

// Ranking table headers by position and season
export const rankingHeaders = {
  // 2024 Season default headers
  season2024: {
    headerType: "2024 Season",
    headers: ["#", "Player Name", "Half PPR Pts"],
  },
  
  // 2024 Season position-specific headers
  QB_2024: {
    headerType: "2024 Season",
    headers: [
      '#',   
      'Player', 
      'Half PPR Pts',
      'Pass Attempts', 
      'Completions', 
      'Passing Yards', 
      'Passing TDs', 
      'Interceptions', 
      'Completion %',
      'Rush Attempts'
    ],
  },
  
  RB_2024: {
    headerType: "2024 Season",
    headers: [
      '#', 
      'Player', 
      'Half PPR Pts',
      'Rushing Attempts', 
      'Rushing Yards', 
      'Rushing TDs', 
      'Yds per Rush', 
      'Receptions', 
      'Receiving Yards', 
      'Team Rush Share'
    ],
  },
  
  WR_2024: {
    headerType: "2024 Season",
    headers: [
      '#', 
      'Player', 
      'Half PPR Pts',
      'Target Total', 
      'Reception Total', 
      'Rec Yards Total', 
      'Receiving TDs', 
      'Catch Rate', 
      'Avg Depth', 
      'Team Target Share'
    ],
  },
  
  TE_2024: {
    headerType: "2024 Season",
    headers: [
      '#', 
      'Player', 
      'Half PPR Pts',
      'Target Total', 
      'Reception Total', 
      'Rec Yards Total', 
      'Receiving TDs', 
      'Catch Rate', 
      'Avg YAC', 
      'Team Target Share'
    ],
  },
  
  // 2025 Season default headers
  season2025: {
    headerType: "2025 Season",
    headers: ["#", "Player Name", "ADP", "Dynasty Score"],
  },
  
  // 2025 Season position-specific headers
  QB_2025: {
    headerType: "2025 Season",
    headers: [
      '#',   
      'Player', 
      'ADP', 
      "Dynasty Score",
      'P Att', 
      'P Cmp', 
      'P Yds', 
      'P TD', 
      'Int', 
      'Sacks', 
      'P Yds/Att',
      'Rush Att',
      'Rush Yds',
      'Rush TD',
      'FPTS (PPR)'
    ],
  },
  
  RB_2025: {
    headerType: "2025 Season",
    headers: [
      '#', 
      'Player', 
      'ADP', 
      'Dynasty Score',
      'Carries', 
      'Rush Yds', 
      'Rush TD', 
      'Yds/Carry', 
      'Rush %', 
      'Targets', 
      'Rec', 
      'Rec Yds', 
      'Rec TD', 
      'Touches', 
      'FPTS (PPR)'
    ],
  },
  
  WR_2025: {
    headerType: "2025 Season",
    headers: [
      '#', 
      'Player', 
      'ADP', 
      'Dynasty Score',
      'Targets', 
      'Tgt %', 
      'Rec', 
      'Catch %', 
      'Rec Yds', 
      'Yds/Rec', 
      'Yds/Tgt', 
      'Rec TD', 
      'FPTS (PPR)'
    ],
  },
  
  TE_2025: {
    headerType: "2025 Season",
    headers: [
      '#', 
      'Player', 
      'ADP', 
      'Dynasty Score',
      'Targets', 
      'Tgt %', 
      'Rec', 
      'Catch %', 
      'Rec Yds', 
      'Yds/Rec', 
      'Rec TD', 
      'FPTS (PPR)'
    ],
  },
  
  ALL_2025: {
    headerType: "2025 Season",
    headers: [
      '#',
      'Player',
      'ADP',
      'Dynasty Score',
      'Touches',
      'Rush Yds',
      'Rush TD',
      'Targets',
      'Rec',
      'Rec Yds',
      'Rec TD',
      'Pass Yds',
      'Pass TD',
      'FPTS (PPR)',
      'FPTS (Std)'
    ],
  }
};

// Signout utility function - same functionality as AuthContext signout
export const signout = async () => {
  // Create auth store instance (same as in AuthContext)
  const useAuthStore = create<any>(
    persist(
      (set, get) => ({
        user: null,
        setUser: (user: any) => set({ user }),
        clearUser: () => set({ user: null }),
        profile: null,
        setProfile: (profile: any) => set({ profile }),
        clearProfile: () => set({ profile: null }),
        setAppleProfile: (appleProfile: any) => set({ appleProfile }),
        clearAppleProfile: () => set({ appleProfile: null }),
        distinctId: null,
        setDistinctId: (id: string) => set({ distinctId: id }),
      }),
      {
        name: "auth-token",
        getStorage: () => localStorage,
      },
    ),
  );

  const { error } = await supabase.auth.signOut();
  if (error) {
    console.error("error on during logout ", error);
  } else {
    // Clear user data
    useAuthStore.getState().clearUser();
    useAuthStore.getState().clearProfile();

    // Reset the paywall flag on logout - access store directly
    usePaywallStore.getState().clearPaywallState();
    
    // Clear league data - access store directly
    useLeagueStore.getState().clearSelectedLeague();
    useLeagueStore.getState().clearLeagueType();
    useLeagueStore.getState().clearScoringFormat();
    useAdvancedStatsPromoStore.getState().clearPromoState();
    useBookaCallStore.getState().resetCount();

    // Create a new anonymous ID for this device
    const newAnonId = `anon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    useAuthStore.getState().setDistinctId(newAnonId);

    // Reset Mixpanel for anonymous tracking
    MixpanelService.reset();
    MixpanelService.identifyUser(newAnonId);

    console.log("logout successfully");
  }
};


